<template>
  <epc-modal-wrapper
    :active="active"
    :title="title"
    :width="560"
    :can-cancel="['escape', 'outside']"
    scroll-body
    @update:active="_OnActiveChange"
  >
    <div class="cert-form">
      <!-- Entidad (una certificación por entidad; en edición queda fijada) -->
      <b-field :label="$t('entity')" class="mb-4">
        <b-select v-model="form.entityType" expanded :disabled="isEdit" @input="_OnEntityChange">
          <option v-for="e in availableEntityOptions" :key="e.value" :value="e.value">
            {{ $t(e.label) }}
          </option>
        </b-select>
      </b-field>

      <!-- Fila 1: Periodo | Certifica por -->
      <div class="grid grid-cols-2 gap-4 mb-0">
        <b-field :label="$t('period')">
          <b-select v-model="form.period" expanded>
            <option v-for="p in periodOptions" :key="p.value" :value="p.value">
              {{ $t(p.label) }}
            </option>
          </b-select>
        </b-field>
        <b-field :label="$t('certifies_by')">
          <b-select v-model="form.certifiesBy" expanded :disabled="isCivil" @input="_OnCertifiesByChange">
            <option v-for="c in certifiesByOptions" :key="c.value" :value="c.value">
              {{ $t(c.label) }}
            </option>
          </b-select>
        </b-field>
      </div>

      <!-- Fila 2: Fecha inicio | % producción -->
      <div class="grid grid-cols-2 gap-4 mb-0">
        <b-field :label="$t('certification_start_date')">
          <b-datepicker
            v-model="form.startDate"
            icon-pack="fas"
            icon="calendar"
            :placeholder="$t('select')"
            :month-names="$t('months_names')"
            :day-names="$t('days_names')"
            :first-day-of-week="parseInt($t('first_day_of_week'))"
            append-to-body
            :mobile-native="false"
            position="is-bottom-left"
          />
        </b-field>
        <b-field :label="$t('percentage')">
          <b-input
            v-model.number="form.productionPercentage"
            type="number"
            min="0"
            :max="isPercentageLocked ? 100 : 99.99"
            step="0.01"
            :disabled="isPercentageLocked"
            expanded
          />
        </b-field>
      </div>

      <!-- Fila 3: Día de certificación (1-31) | Idioma del documento -->
      <div class="grid grid-cols-2 gap-4" :class="{ 'mb-4': isElectrical }">
        <b-field :label="$t('certification_day')" :message="$t('certification_day_hint')">
          <b-input
            v-model.number="form.certificationDay"
            type="number"
            min="1"
            max="31"
            step="1"
            expanded
          />
        </b-field>
        <b-field :label="$t('document_language')" :message="$t('document_language_hint')">
          <b-select v-model="form.documentLanguage" expanded>
            <option v-for="l in languageOptions" :key="l.value" :value="l.value">
              {{ $t(l.label) }}
            </option>
          </b-select>
        </b-field>
        <!-- Moneda del documento; vacío = usar la del proyecto. La leen tabla y PDF al generar. -->
        <b-field :label="$t('select_currency')" :message="$t('currency_document_hint')">
          <b-select v-model="form.currency" expanded>
            <option :value="null">{{ $t('project_currency_default') }}</option>
            <option v-for="c in currencyOptions" :key="c.value" :value="c.value">
              {{ c.label }}
            </option>
          </b-select>
        </b-field>
      </div>

      <!-- Fila 4: Tipo de certificación (alcance por metros) — solo instalación eléctrica / zanjas. -->
      <div v-if="isElectrical" class="grid grid-cols-2 gap-4 mb-4">
        <b-field>
          <template #label>
            <span class="cert-label-with-help">
              {{ $t('certification_type') }}
              <b-tooltip
                :label="$t('certification_type_hint')"
                position="is-left"
                type="is-dark"
                multilined
                size="is-medium"
              >
                <b-icon pack="fas" icon="info-circle" size="is-small" class="cert-help-icon" />
              </b-tooltip>
            </span>
          </template>
          <b-select v-model="form.certifyScope" expanded>
            <option v-for="s in certifyScopeOptions" :key="s.value" :value="s.value">
              {{ $t(s.label) }}
            </option>
          </b-select>
        </b-field>
      </div>

      <!-- Envío de correos -->
      <div class="cert-settings-dialog__switch-row">
        <div class="cert-settings-dialog__switch-text">
          <p class="cert-settings-dialog__switch-label">{{ $t('enable_certification_emails') }}</p>
          <p class="cert-settings-dialog__switch-hint">{{ $t('enable_certification_emails_hint') }}</p>
        </div>
        <b-switch v-model="form.emailsEnabled" type="is-info" />
      </div>

      <!-- Días antes del recordatorio (solo activo si correos habilitados) -->
      <div class="mt-4" :class="{ 'cert-settings-dialog__field--disabled': !form.emailsEnabled }">
        <b-field
          :label="$t('reminder_days_before')"
          :message="$t('reminder_days_before_hint')"
        >
          <b-input
            v-model.number="form.reminderDaysBefore"
            type="number"
            min="0"
            max="10"
            step="1"
            expanded
            :disabled="!form.emailsEnabled"
          />
        </b-field>
      </div>
    </div>

    <template #footer>
      <button type="button" class="epc-modal__button" @click="_HandleCancel">
        {{ $t('cancel') }}
      </button>
      <button
        type="button"
        class="epc-modal__button epc-modal__button--primary"
        :disabled="isSaving"
        @click="_HandleSave"
      >
        {{ isSaving ? $t('saving') : $t('save') }}
      </button>
    </template>
  </epc-modal-wrapper>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import EpcModalWrapper from '·/components/epc-modal-wrapper.vue';

// Valores int que casan con los enums del backend.
const ENTITY_CIVIL = 1;        // PartnerProcessEntity::CivilWorks
const ENTITY_ELECTRICAL = 3;   // PartnerProcessEntity::ElectricalInstallation
const CERTIFIES_PRODUCTION = 1; // PartnerProcessCertifiesBy::Production
const CERTIFIES_QUALITY = 2;    // PartnerProcessCertifiesBy::Quality
// PartnerProcessCertifyScope (partial=1 incluir parciales, complete-only=2 solo 100%).
const CERTIFY_SCOPE_PARTIAL = 1;
const CERTIFY_SCOPE_COMPLETE = 2;

export default {
  name: 'SubcontractorCertificationSettingsModal',

  components: { EpcModalWrapper },

  props: {
    // Control de apertura del modal (wrapper). El padre lo sincroniza.
    active: { type: Boolean, default: true },
    title: { type: String, default: '' },
    projectId: { type: Number, default: null },
    partnerId: { type: Number, default: null },
    // Registro existente a editar (null = crear nuevo).
    process:   { type: Object, default: null },
    // entity_type (ints) ya configurados — para filtrar entidades libres al crear.
    usedEntities: { type: Array, default: () => [] },
  },

  data() {
    return {
      isSaving: false,
      // Entidades (enum PartnerProcessEntity: obra civil=1, montaje mecánico=2, instalación eléctrica=3).
      entityOptions: [
        { value: 1, label: 'entity_civil_works' },
        { value: 2, label: 'entity_mechanical_assembly' },
        { value: 3, label: 'entity_electrical_installation' },
      ],
      // enum PartnerProcessPeriod (daily=1, weekly=2, biweekly=3, monthly=4, bimonthly=8).
      periodOptions: [
        { value: 1, label: 'daily' },
        { value: 2, label: 'weekly' },
        { value: 3, label: 'biweekly' },
        { value: 4, label: 'monthly' },
        { value: 8, label: 'bimonthly' },
      ],
      // enum PartnerProcessCertifiesBy (production=1, quality=2).
      certifiesByOptions: [
        { value: CERTIFIES_PRODUCTION, label: 'production' },
        { value: CERTIFIES_QUALITY, label: 'quality' },
      ],
      // enum PartnerProcessCertifyScope (incluir parciales=1, solo completas=2). Solo eléctrico.
      certifyScopeOptions: [
        { value: CERTIFY_SCOPE_PARTIAL, label: 'certify_include_partial' },
        { value: CERTIFY_SCOPE_COMPLETE, label: 'certify_complete_only' },
      ],
      // Idiomas en que se puede generar el documento de certificación.
      languageOptions: [
        { value: 'es', label: 'spanish' },
        { value: 'en', label: 'english' },
        { value: 'pt', label: 'portuguese' },
        { value: 'it', label: 'italian' },
      ],
      // Monedas soportadas (símbolo). null = usar la del proyecto.
      currencyOptions: [
        { value: '€', label: '€ (EUR)' },
        { value: '$', label: '$ (USD)' },
        { value: 'R$', label: 'R$ (BRL)' },
        { value: '£', label: '£ (GBP)' },
      ],
      form: {
        entityType:           null,
        period:               null,
        certifiesBy:          null,
        startDate:            null,
        certificationDay:     null,
        productionPercentage: null,
        certifyScope:         CERTIFY_SCOPE_PARTIAL,
        emailsEnabled:        false,
        reminderDaysBefore:   2,
        documentLanguage:     null,
        currency:             null, // null = moneda del proyecto
      },
    };
  },

  computed: {
    isEdit() {
      return !!(this.process && this.process.id);
    },
    // Obra Civil: siempre Producción al 100% (campos bloqueados).
    isCivil() {
      return this.form.entityType === ENTITY_CIVIL;
    },
    // Instalación eléctrica (y zanjas): certificación por metros → aplica el alcance parcial/completo.
    isElectrical() {
      return this.form.entityType === ENTITY_ELECTRICAL;
    },
    isQualitySelected() {
      return this.form.certifiesBy === CERTIFIES_QUALITY;
    },
    // El % se bloquea en Obra Civil (100) y en Calidad (100).
    isPercentageLocked() {
      return this.isCivil || this.isQualitySelected;
    },
    // Al crear solo se ofrecen entidades libres; al editar la entidad va fija.
    availableEntityOptions() {
      if (this.isEdit) {
        return this.entityOptions;
      }
      return this.entityOptions.filter(e => !this.usedEntities.includes(e.value));
    },
  },

  mounted() {
    if (this.isEdit) {
      this.form.entityType = this.process.entity_type;
      this.form.period = this.process.period;
      this.form.certifiesBy = this.process.certifies_by;
      this.form.startDate = this.process.start_date ? new Date(this.process.start_date) : null;
      this.form.certificationDay = this.process.certification_day || null;
      this.form.productionPercentage = (this.process.progress !== null && this.process.progress !== undefined)
        ? parseFloat(this.process.progress)
        : null;
      const scope = Number(this.process.certify_scope);
      this.form.certifyScope = (scope === CERTIFY_SCOPE_COMPLETE) ? CERTIFY_SCOPE_COMPLETE : CERTIFY_SCOPE_PARTIAL;
      this.form.emailsEnabled = !!this.process.emails_enabled;
      const reminderDays = Number(this.process.reminder_days_before);
      this.form.reminderDaysBefore = Number.isInteger(reminderDays) ? reminderDays : 2;
      // Moneda configurada (null = usa la del proyecto).
      this.form.currency = this.process.currency || null;
    }

    // Idioma del documento: el configurado; si no hay (config antigua o alta nueva),
    // se preselecciona el idioma actual del usuario.
    const configured = this.isEdit ? this.process.document_language : null;
    const current = (this.$i18n && this.$i18n.locale) || 'es';
    this.form.documentLanguage = configured || (this.languageOptions.some(l => l.value === current) ? current : 'es');
  },

  methods: {
    // Al elegir entidad: Obra Civil fuerza Producción + 100; el resto se limpia.
    _OnEntityChange(value) {
      if (value === ENTITY_CIVIL) {
        this.form.certifiesBy = CERTIFIES_PRODUCTION;
        this.form.productionPercentage = 100;
      } else {
        this.form.certifiesBy = null;
        this.form.productionPercentage = null;
      }
    },

    // Calidad → bloquea y fija 100; Producción → desbloquea y limpia.
    _OnCertifiesByChange(value) {
      if (value === CERTIFIES_QUALITY) {
        this.form.productionPercentage = 100;
      } else if (value === CERTIFIES_PRODUCTION) {
        this.form.productionPercentage = null;
      }
    },

    async _HandleSave() {
      if (!this.projectId || !this.partnerId) {
        return;
      }
      if (!this.form.entityType || !this.form.period || !this.form.certifiesBy || !this.form.startDate) {
        this.$notify.warning('required_fields');
        return;
      }

      // Día de certificación: entero entre 1 y 31.
      const day = Number(this.form.certificationDay);
      if (!this.form.certificationDay || !Number.isInteger(day) || day < 1 || day > 31) {
        this.$notify.warning('certification_day_range');
        return;
      }

      // Días de recordatorio: 0-10 (0 = mismo día de la certificación).
      let reminderDays = Number(this.form.reminderDaysBefore);
      if (this.form.emailsEnabled) {
        if (
          this.form.reminderDaysBefore === null ||
          this.form.reminderDaysBefore === '' ||
          !Number.isInteger(reminderDays) ||
          reminderDays < 0 ||
          reminderDays > 10
        ) {
          this.$notify.warning('reminder_days_before_range');
          return;
        }
      } else {
        reminderDays = Number.isInteger(reminderDays) ? reminderDays : 2;
        if (reminderDays < 0 || reminderDays > 10) {
          reminderDays = 2;
        }
      }

      // Mecánico/Eléctrico con Producción: el % debe ser número válido y MENOR a 100.
      if (!this.isCivil && this.form.certifiesBy === CERTIFIES_PRODUCTION) {
        const pct = Number(this.form.productionPercentage);
        if (
          this.form.productionPercentage === null ||
          this.form.productionPercentage === '' ||
          Number.isNaN(pct) ||
          pct < 0 ||
          pct >= 100
        ) {
          this.$notify.warning('production_percentage_below_100');
          return;
        }
      }

      this.isSaving = true;
      try {
        const payload = {
          entity_type:           this.form.entityType,
          certifies_by:          this.form.certifiesBy,
          period:                this.form.period,
          start_date:            moment(this.form.startDate).format('YYYY-MM-DD'),
          certification_day:     this.form.certificationDay,
          progress:              this.form.productionPercentage,
          // Solo se envía en eléctrico; en el resto el backend usa el default (incluir parciales).
          certify_scope:         this.isElectrical ? this.form.certifyScope : CERTIFY_SCOPE_PARTIAL,
          emails_enabled:        !!this.form.emailsEnabled,
          reminder_days_before:  reminderDays,
          document_language:     this.form.documentLanguage,
          currency:              this.form.currency,
        };
        const { data } = await axios.post(
          `/api/v2/project/${this.projectId}/partners/${this.partnerId}/certification-processes`,
          payload
        );
        if (data && data.success) {
          this.$notify.success('success');
          this.$emit('saved', data.process);
          this.$emit('close');
        } else {
          this.$notify.error('error');
        }
      } catch (e) {
        this.$notify.error('error');
      } finally {
        this.isSaving = false;
      }
    },

    _HandleCancel() {
      this.$emit('close');
    },

    // El wrapper emite update:active=false al cerrarse (escape / x / click fuera).
    _OnActiveChange(value) {
      if (!value) {
        this.$emit('close');
      }
    },
  },
};
</script>

<style lang="scss">
/* El chrome del modal (dialog/header/body/footer/botones) lo aporta EpcModalWrapper
   (epc-modal.scss). Aquí solo estilos de contenido específicos de este formulario. */

/* Compacta los campos: Buefy/Bulma dejan mucho hueco entre label y control. */
.cert-form {
  .field:not(:last-child) {
    margin-bottom: 0.75rem;
  }
  .label {
    font-size: 0.72rem;
    font-weight: 600;
    color: #4b5563;
    margin-bottom: 0.2rem;
    line-height: 1.2;
  }
  .help {
    margin-top: 0.15rem;
    font-size: 0.68rem;
  }
}
.cert-settings-dialog__switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 0.9rem;
  margin-bottom: 0.25rem;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
}
.cert-settings-dialog__switch-label {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
}
.cert-settings-dialog__switch-hint {
  margin: 0.2rem 0 0;
  font-size: 0.75rem;
  line-height: 1.35;
  color: #6b7280;
}
.cert-settings-dialog__field--disabled {
  opacity: 0.55;
}
/* Label con icono de ayuda (tooltip) al lado */
.cert-label-with-help {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}
.cert-help-icon {
  color: #9ca3af;
  cursor: help;
  transition: color 150ms ease;
}
.cert-help-icon:hover {
  color: #5577BB;
}
</style>
